import React, { useRef, useEffect } from 'react';
import { translate } from '../../Services/translation';
import ConfiguratorQuantityInput from './ConfiguratorQuantityInput';
import { keyboardNavigationPress } from '../../Services/Wcag';
import { focusFirstIn } from './configuratorFocus';

const ConfiguratorAccessoryGroup = ({
  group,
  groupIndex,
  isOpen,
  isLocked,
  isPastStep,
  moveFocusOnOpen,
  selectedAccessories = [],
  onToggle,
  onRequestEdit,
  onUpdateQuantity,
  onNextStep,
  isLastGroup,
}) => {
  const groupRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (isOpen && groupRef.current) {
      // The button that opened this group unmounts with the previous step, so keyboard focus follows the group.
      if (moveFocusOnOpen) {
        focusFirstIn(listRef.current);
      }
      requestAnimationFrame(() => {
        groupRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  }, [isOpen]);

  const items = group.items || group.accessories || [];
  const totalValuesCount = items.length;

  const groupSelectedItems = items
    .map((acc) => {
      const found = selectedAccessories.find((item) => item.accessory?.id === acc.id);
      return found && found.quantity > 0 ? { ...acc, quantity: found.quantity } : null;
    })
    .filter(Boolean);
    
  const selectedCount = groupSelectedItems.length;
  
  const selectedValueTitle = selectedCount > 0
    ? groupSelectedItems.map((item) => `${item.name}${item.quantity > 1 ? ` (${item.quantity})` : ''}`).join(', ')
    : null;

  const hasBeenPassed = isPastStep ?? (!isOpen && !isLocked);

  const showEditButton = !isOpen && hasBeenPassed;
  // A header holding the edit button stays a plain container, so the two controls are never nested.
  const canToggle = !isLocked && !showEditButton;
  const titleId = `configurator-accessory-group-${groupIndex}-title`;

  return (
    <div
      ref={groupRef}
      className={`configurator__accessory-group ${isLocked ? 'is-locked' : ''}`}
      style={{ opacity: isLocked ? 0.6 : 1 }}
    >
      <div
        className="configurator__option-header"
        onClick={!isLocked ? onToggle : undefined}
        {...(canToggle && {
          role: 'button',
          tabIndex: 0,
          'aria-expanded': isOpen,
          onKeyDown: keyboardNavigationPress(onToggle),
        })}
      >
        <p className="configurator__option-name" id={titleId}>
          {group.label}
        </p>
        {selectedValueTitle && (
          <p className="configurator__option-selected-value">
            {selectedValueTitle}
          </p>
        )}
        {!selectedValueTitle && hasBeenPassed && (
          <p className="configurator__option-selected-value">
            {translate('brenderup.configurator.none.selected') || 'Inga valda'}
          </p>
        )}
        {!selectedValueTitle && !hasBeenPassed && (
          <p className="configurator__option-availability">
            {totalValuesCount} {translate('brenderup.configurator.count.available')}
          </p>
        )}
        {showEditButton && (
          <button
            type="button"
            className="configurator__button-edit"
            onClick={(e) => {
              e.stopPropagation();
              if (onRequestEdit) {
                onRequestEdit();
              } else if (onToggle) {
                onToggle();
              }
            }}
          >
            {translate('brenderup.configurator.button.edit')}
          </button>
        )}
      </div>
      {isOpen && (
        <div
          ref={listRef}
          className="configurator__option-list"
          role="group"
          aria-labelledby={titleId}
        >
          <div className="configurator__accessory-list">
            {items.map((accessory) => {
              const found = selectedAccessories.find(
                (item) => item.accessory?.id === accessory.id
              );
              const currentQuantity = found ? found.quantity : 0;
              const isAdded = currentQuantity > 0;

              return (
                <div
                  key={accessory.id}
                  className={`configurator__accessory-item ${isAdded ? 'is-added' : ''}`}
                >
                  <div className="configurator__accessory-info">
                    {accessory.imageUrl && (
                      <img
                        src={accessory.imageUrl}
                        alt={accessory.name}
                        className="configurator__accessory-image"
                      />
                    )}
                    <div>
                      <p className="configurator__accessory-title">
                        {accessory.name}
                      </p>
                      {accessory.description && (
                        <p className="configurator__accessory-description">
                          {accessory.description}
                        </p>
                      )}
                      {accessory.price && (
                        <p className="configurator__accessory-price">
                          <span>{translate('brenderup.configurator.product.price')}</span>
                          <span>{accessory.formattedPrice}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <ConfiguratorQuantityInput
                    quantity={currentQuantity}
                    onChangeQuantity={(newQty) =>
                      onUpdateQuantity(accessory, newQty)
                    }
                  />
                </div>
              );
            })}
          </div>
          <div className="configurator__option-next-button-container">
            <button
              type="button"
              className="configurator__button-next"
              onClick={onNextStep}
            >
              {isLastGroup
                ? translate('brenderup.configurator.button.finish.configuration')
                : translate('brenderup.configurator.button.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfiguratorAccessoryGroup;